import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Search, ShoppingCart, Plus, Minus, X, CheckCircle, AlertTriangle, Package } from 'lucide-react';
import OrderSummaryBar, { CategoryChips } from '../components/OrderSummaryBar';
import ProductDetailsModal from '../components/ProductDetailsModal';
import SideCart from '../components/SideCart';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

export default function ClientePedido() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [catalogo, setCatalogo] = useState([]);
  const [cart, setCart] = useState({});  // { produto_id: { qty, valor_unitario, produto_nome, unidade_venda } }
  const [search, setSearch] = useState('');
  const [valorMinimo, setValorMinimo] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [observacao, setObservacao] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [detailProduct, setDetailProduct] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'cliente') { navigate('/login'); return; }
    (async () => {
      try {
        const [c, m] = await Promise.all([
          axios.get(`${API}/cliente/me/catalogo`),
          axios.get(`${API}/cliente/me/min-order`)
        ]);
        setCatalogo(c.data);
        setValorMinimo(Number(m.data.valor_minimo) || 0);
      } catch (e) { toast.error('Erro ao carregar catálogo'); }
      setLoading(false);
    })();
  }, [authLoading, user]);

  const categorias = useMemo(() => {
    const count = {};
    catalogo.forEach(p => {
      const c = (p.categoria || '').trim();
      if (c) count[c] = (count[c] || 0) + 1;
    });
    return Object.entries(count).sort((a, b) => b[1] - a[1]).map(([c]) => c);
  }, [catalogo]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return catalogo.filter(p => {
      if (categoriaFiltro && (p.categoria || '') !== categoriaFiltro) return false;
      if (!s) return true;
      return (
        (p.produto || '').toLowerCase().includes(s) ||
        (p.codigo || '').toLowerCase().includes(s) ||
        (p.categoria || '').toLowerCase().includes(s)
      );
    });
  }, [catalogo, search, categoriaFiltro]);

  const total = Object.entries(cart).reduce((s, [, item]) => s + (item.qty * item.valor_unitario), 0);
  const itemsCount = Object.values(cart).reduce((s, item) => s + item.qty, 0);

  const addToCart = (p, opts = {}) => {
    const valor = p.preco_unitario_venda || p.valor_por_unidade || p.preco_unitario_referencia || 0;
    const foto = (p.fotos && p.fotos[0]) || null;
    setCart(prev => {
      const cur = prev[p.id] || {
        qty: 0, valor_unitario: valor,
        produto: p.produto, produto_nome: p.produto,
        unidade_venda: p.unidade_venda,
        foto, cor: opts.cor || '', tamanho: opts.tamanho || '',
      };
      return {
        ...prev,
        [p.id]: {
          ...cur, qty: cur.qty + 1,
          cor: opts.cor || cur.cor || '',
          tamanho: opts.tamanho || cur.tamanho || '',
        },
      };
    });
  };
  const removeItem = (pid) => setCart(prev => { const { [pid]: _rm, ...rest } = prev; return rest; });
  const decreaseItem = (pid) => setCart(prev => {
    const cur = prev[pid];
    if (!cur) return prev;
    if (cur.qty <= 1) { const { [pid]: _, ...rest } = prev; return rest; }
    return { ...prev, [pid]: { ...cur, qty: cur.qty - 1 } };
  });
  const setQty = (pid, qty) => {
    const n = Math.max(0, parseInt(qty || '0', 10));
    if (n === 0) setCart(prev => { const { [pid]: _, ...rest } = prev; return rest; });
    else setCart(prev => ({ ...prev, [pid]: { ...prev[pid], qty: n } }));
  };

  const handleSubmit = async () => {
    const items = Object.entries(cart).map(([pid, item]) => ({
      produto_id: pid,
      produto_nome: item.produto_nome,
      quantidade: item.qty,
      valor_unitario: item.valor_unitario,
      unidade_venda: item.unidade_venda,
      cor: item.cor || null,
      tamanho: item.tamanho || null,
    }));
    if (items.length === 0) { toast.error('Adicione pelo menos um produto'); return; }
    if (valorMinimo > 0 && total < valorMinimo) {
      toast.error(`Pedido mínimo: ${fmtBRL(valorMinimo)}. Faltam ${fmtBRL(valorMinimo - total)}`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/cliente/me/pedido`, { items, observacao });
      toast.success(`Pedido #${res.data.numero_pedido} enviado! O admin vai confirmar em breve.`, { duration: 6000 });
      setCart({});
      setObservacao('');
      navigate(`/cliente/romaneio/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar pedido');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="portal-page-bg min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800 pb-24">
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/cliente/dashboard')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="flex-1 font-bold text-gray-900 dark:text-white">Novo Pedido</h1>
          <button onClick={() => setShowCart(true)} className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-testid="open-cart-btn">
            <ShoppingCart className="w-5 h-5" />
            {itemsCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-emerald-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{itemsCount}</span>
            )}
          </button>
        </div>

        {/* Min order banner */}
        {valorMinimo > 0 && (
          <div className={`px-4 py-2 text-xs font-semibold flex items-center justify-between gap-2 ${
            total >= valorMinimo ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'
          }`} data-testid="cliente-min-banner">
            <span className="flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Pedido mínimo: {fmtBRL(valorMinimo)}
            </span>
            <span>
              {total >= valorMinimo ? '✓ Atingido' : `Faltam ${fmtBRL(valorMinimo - total)}`}
            </span>
          </div>
        )}

        {/* Search */}
        <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar produto, código ou categoria..."
              className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none"
              data-testid="catalog-search"
            />
          </div>
        </div>

        {/* Filtro por categoria */}
        {categorias.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700">
            <CategoryChips
              categorias={categorias}
              selected={categoriaFiltro}
              onSelect={setCategoriaFiltro}
              color="emerald"
              testidPrefix="cliente-category"
            />
          </div>
        )}
      </header>

      {/* Catálogo */}
      <div className="max-w-7xl mx-auto px-3 py-4 flex gap-4 items-start">
        <div className="flex-1 min-w-0">
        {filtered.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">Nenhum produto encontrado.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="catalog-grid">
            {filtered.map(p => {
              const foto = (p.fotos && p.fotos[0]) || null;
              const inCart = cart[p.id]?.qty || 0;
              const valor = p.preco_unitario_venda || p.valor_por_unidade || p.preco_unitario_referencia || 0;
              return (
                <div
                  key={p.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setDetailProduct(p)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDetailProduct(p); }}
                  className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col cursor-pointer hover:shadow-lg hover:border-emerald-300 transition"
                  data-testid={`product-card-${p.id}`}
                  title="Clique para ver detalhes do produto"
                >
                  <div className="aspect-square bg-gray-100 dark:bg-gray-900 relative w-full">
                    {foto ? (
                      <img src={foto} alt={p.produto} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <Package className="w-12 h-12" />
                      </div>
                    )}
                    {p.estoque > 0 && (
                      <span className="absolute top-2 right-2 text-[10px] px-1.5 py-0.5 bg-black/60 text-white rounded-full">
                        {p.estoque} {p.unidade_venda || 'un'}
                      </span>
                    )}
                  </div>
                  <div className="p-3 flex-1 flex flex-col" onClick={(e) => e.stopPropagation()}>
                    <p className="text-[10px] text-gray-500 uppercase">{p.codigo}</p>
                    <button
                      type="button"
                      onClick={() => setDetailProduct(p)}
                      className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2 mb-1 text-left hover:text-emerald-600 transition"
                    >
                      {p.produto}
                    </button>
                    <p className="text-lg font-bold text-emerald-600 mt-auto">{fmtBRL(valor)}</p>
                    <p className="text-[10px] text-gray-500">por {p.unidade_venda || 'unidade'}{p.quantidade_por_unidade > 1 ? ` (${p.quantidade_por_unidade} un)` : ''}</p>

                    {inCart === 0 ? (
                      <button
                        onClick={() => addToCart(p)}
                        className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1"
                        data-testid={`add-to-cart-${p.id}`}
                      >
                        <Plus className="w-3.5 h-3.5" /> Adicionar
                      </button>
                    ) : (
                      <div className="mt-2 flex items-center gap-1">
                        <button onClick={() => decreaseItem(p.id)} className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 p-1.5 rounded-lg" data-testid={`decrease-${p.id}`}>
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <input
                          type="number"
                          min="0"
                          value={inCart}
                          onChange={(e) => setQty(p.id, e.target.value)}
                          className="flex-1 text-center text-sm border border-gray-300 dark:border-gray-600 rounded-lg py-1 bg-white dark:bg-gray-900"
                          data-testid={`qty-${p.id}`}
                        />
                        <button onClick={() => addToCart(p)} className="bg-emerald-600 hover:bg-emerald-700 text-white p-1.5 rounded-lg" data-testid={`increase-${p.id}`}>
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>

        {/* Side Cart (desktop lg+) */}
        <SideCart
          cart={cart}
          onRemove={removeItem}
          onDec={decreaseItem}
          onInc={(pid) => addToCart(catalogo.find(p => p.id === pid) || { id: pid })}
          onCheckout={() => setShowCart(true)}
          total={total}
          itemsCount={itemsCount}
          valorMinimo={valorMinimo}
          color="emerald"
          testidPrefix="cliente-side-cart"
        />
      </div>

      {/* Floating Cart Bar */}
      {itemsCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t-2 border-emerald-500 shadow-2xl z-20 px-4 py-3" data-testid="cart-bar">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-gray-500">{itemsCount} item(s)</p>
              <p className="text-lg font-bold text-emerald-700">{fmtBRL(total)}</p>
            </div>
            <button
              onClick={() => setShowCart(true)}
              className="flex-1 max-w-xs bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 text-white py-2.5 rounded-lg font-bold flex items-center justify-center gap-2"
              data-testid="review-cart-btn"
            >
              <ShoppingCart className="w-4 h-4" /> Revisar Pedido
            </button>
          </div>
        </div>
      )}

      {/* Cart Modal */}
      {showCart && (
        <div className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowCart(false)} data-testid="cart-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-gray-800 w-full max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800">
              <h2 className="font-bold">Seu Pedido</h2>
              <button onClick={() => setShowCart(false)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-testid="close-cart">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-4 py-2">
              {Object.entries(cart).length === 0 ? (
                <p className="text-center text-sm text-gray-500 py-6">Carrinho vazio</p>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {Object.entries(cart).map(([pid, item]) => (
                    <div key={pid} className="py-3 flex items-center justify-between gap-2" data-testid={`cart-item-${pid}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{item.produto_nome}</p>
                        <p className="text-xs text-gray-500">{item.qty} × {fmtBRL(item.valor_unitario)}</p>
                      </div>
                      <p className="text-sm font-bold">{fmtBRL(item.qty * item.valor_unitario)}</p>
                      <button onClick={() => setCart(prev => { const { [pid]: _, ...rest } = prev; return rest; })} className="p-1 text-red-500 hover:bg-red-50 rounded">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3">
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Observação (opcional)</label>
                <textarea
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                  placeholder="Ex: prazo de entrega preferido, instruções especiais..."
                  rows={2}
                  className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none"
                  data-testid="cart-observacao"
                />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm">Total</span>
                <span className="text-xl font-bold text-emerald-700">{fmtBRL(total)}</span>
              </div>
              {valorMinimo > 0 && total < valorMinimo && (
                <p className="text-xs text-amber-700 mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Falta {fmtBRL(valorMinimo - total)} para o pedido mínimo
                </p>
              )}
              <button
                onClick={handleSubmit}
                disabled={submitting || itemsCount === 0 || (valorMinimo > 0 && total < valorMinimo)}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 disabled:from-gray-400 disabled:to-gray-400 text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2"
                data-testid="submit-pedido-btn"
              >
                {submitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {submitting ? 'Enviando...' : 'Enviar Pedido para Admin'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalhes do produto */}
      {detailProduct && (
        <ProductDetailsModal
          product={detailProduct}
          onClose={() => setDetailProduct(null)}
          onAdd={addToCart}
          color="emerald"
          showPrice
        />
      )}

      {/* Barra flutuante com progresso do pedido mínimo */}
      {!showCart && (
        <OrderSummaryBar
          itemsCount={itemsCount}
          total={total}
          valorMinimo={valorMinimo}
          onOpenCart={() => setShowCart(true)}
          color="emerald"
          testidPrefix="cliente-summary"
        />
      )}
    </div>
  );
}
